import type { GuideArticle } from '../data/guides';
import { getGuideBySlug } from '../data/guides';
import { buildHelpCenterFaqJsonLd } from '../data/helpCenterFaqSchema';
import {
  getLocalLandingPageSeo,
  localLandingCustomerServicePath,
  resolveLocalLandingFromPathname,
  type LocalLandingResolved,
} from './localSeoLanding';
import { buildLocalLandingFaqMainEntity } from './localSeoLandingSeoEnhancer';
import { getPageSeo } from './seoConfig';

const JSON_LD_ID = 'virtoly-page-json-ld';

export function injectPageJsonLd(data: object): void {
  if (typeof document === 'undefined') return;
  let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = JSON_LD_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function removePageJsonLd(): void {
  document.getElementById(JSON_LD_ID)?.remove();
}

export function buildGuideArticleJsonLd(article: GuideArticle, pageUrl: string): object {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: article.title,
        description: article.metaDescription,
        isPartOf: { '@id': `${origin}/#website` },
      },
      {
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        headline: article.title,
        description: article.metaDescription,
        datePublished: article.publishedAt,
        dateModified: article.updatedAt ?? article.publishedAt,
        author: { '@type': 'Organization', name: 'Virtoly', url: origin },
        publisher: { '@type': 'Organization', name: 'Virtoly', url: origin },
        mainEntityOfPage: { '@id': `${pageUrl}#webpage` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Guides', item: `${origin}/guides` },
          { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: article.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };
}

export function buildLocalLandingJsonLd(resolved: LocalLandingResolved, pageUrl: string): object {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const seo = getLocalLandingPageSeo(resolved);
  const { cityName, provinceName, category, subcategory } = resolved;
  const serviceLabel = subcategory ? subcategory.name : category.serviceHeading;
  const bookUrl = `${origin}${localLandingCustomerServicePath(resolved)}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${pageUrl}#organization`,
        name: 'Virtoly',
        url: origin,
      },
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: seo.title,
        description: seo.description,
        isPartOf: { '@id': `${pageUrl}#organization` },
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: `${serviceLabel} in ${cityName}`,
        serviceType: serviceLabel,
        description: seo.description,
        url: pageUrl,
        provider: { '@id': `${pageUrl}#organization` },
        areaServed: [
          {
            '@type': 'City',
            name: cityName,
            containedInPlace: {
              '@type': 'AdministrativeArea',
              name: provinceName,
            },
          },
        ],
        offers: {
          '@type': 'Offer',
          url: bookUrl,
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: buildLocalLandingFaqMainEntity(resolved),
      },
    ],
  };
}

export function buildCategoryServiceJsonLd(
  pageUrl: string,
  seo: { title: string; description: string },
): object {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const serviceName = seo.title.replace(/ \| Virtoly$/, '');

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: seo.title,
        description: seo.description,
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: serviceName,
        serviceType: serviceName,
        description: seo.description,
        url: pageUrl,
        provider: {
          '@type': 'Organization',
          name: 'Virtoly',
          url: origin,
        },
        areaServed: {
          '@type': 'Country',
          name: 'Canada',
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/services` },
          { '@type': 'ListItem', position: 3, name: serviceName, item: pageUrl },
        ],
      },
    ],
  };
}

/** Page-specific JSON-LD for client-side route changes (mirrors server PageMetaResolver). */
export function jsonLdForPath(pathname: string, search = ''): object | null {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}${normalized === '/' ? '' : normalized}`;

  if (normalized === '/help-center') {
    return buildHelpCenterFaqJsonLd(pageUrl);
  }

  const guideMatch = normalized.match(/^\/guides\/([^/]+)$/);
  if (guideMatch) {
    const article = getGuideBySlug(guideMatch[1]);
    if (article) {
      return buildGuideArticleJsonLd(article, pageUrl);
    }
  }

  const localResolved = resolveLocalLandingFromPathname(normalized);
  if (localResolved) {
    return buildLocalLandingJsonLd(localResolved, pageUrl);
  }

  const customerMatch = normalized.match(/^\/customer-service\/([^/]+)$/);
  if (customerMatch) {
    const seo = getPageSeo(pathname, search);
    if (seo) {
      return buildCategoryServiceJsonLd(pageUrl, seo);
    }
  }

  return null;
}
